import type { VenueResult } from './VenuePicker';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { LoadingState } from '@/components/ui/async-state';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { AppScreen } from '@/components/ui/scene';
import { Text } from '@/components/ui/text';
import { APIError } from '@/lib/api/api';
import { authStore, useAuth } from '@/lib/auth/auth';
import { handleReadOnlyMarketError } from '@/features/market/readOnlyMarket';
import { getEventHref } from '@/lib/navigation/routes';
import {
  addSentryActionBreadcrumb,
  captureSentryException,
} from '@/lib/analytics/sentry';
import type {
  GeocodeSuggestion,
  PatchEventPosterFocalPointRequest,
} from '@/lib/api/apiTypes';
import {
  EVENT_POSTER_CONSTANTS,
  EntryTypeTicketed,
  EventStatusActive,
  OrganizationPlanFeatureAIFlyerExtraction,
} from '@/lib/api/apiTypes';
import { DESIGN_TOKENS } from '@/lib/design/theme';
import {
  hasIncludedOrganizationFeature,
} from '@/features/events/eventMobileHelpers';
import { locationStore } from '@/lib/location/location';
import { useAccountContext } from '@/features/workspaces/useAccountContext';
import { useEventAttributeCatalog } from '@/features/events/useEventAttributeCatalog';
import { useEventTagCatalog } from '@/features/events/useEventTagCatalog';
import { useOrganization } from '@/features/workspaces/useOrganization';
import {
  trackEventEdited,
  trackLaunchEvent,
  trackEventPublished,
} from '@/lib/analytics/posthog';
import {
  getEventEditorStepIssues,
  validateEventEditorDraft,
  validateEventEditorStep,
} from '@/features/events/editor/eventEditorValidation';
import { useWritableMarketGuard } from '@/features/market/useWritableMarketGuard';
import {
  extractFlyerFromImage,
} from '@/features/events/editor/flyerCurator';
import { DateTimePickerAndroid } from '@react-native-community/datetimepicker';
import * as ImagePicker from 'expo-image-picker';
import type { ImagePickerAsset } from 'expo-image-picker';
import { Redirect, router } from 'expo-router';
import {
  AlertCircleIcon,
  ArrowLeftIcon,
  InfoIcon,
  MoveRightIcon,
} from 'lucide-react-native';
import { useIsDesktop } from '@/hooks/useBreakpoint';
import { FinishStep } from '@/features/events/editor/FinishStep';
import { PitchStep } from '@/features/events/editor/PitchStep';
import { PosterFocalPointModal } from '@/features/events/editor/PosterFocalPointModal';
import { ScheduleStep } from '@/features/events/editor/ScheduleStep';
import { STEPS } from '@/features/events/editor/constants';
import type {
  ActivePosterCropEditor,
  DraftPoster,
  EventDraft,
  EventEditorProps,
  EventMutationPayload,
  FlyerDraftMetadata,
  FlyerOverrideField,
  PickerField,
} from '@/features/events/editor/types';
import {
  applyPickerValue,
  buildDateTime,
  buildEventDraft,
  buildUploadingDraftPoster,
  getEditedEventFields,
  getInitialSchedule,
  getPickerMode,
  getPickerValue,
  hasCoordinate,
  hasPartialDateTime,
  humanizeEventEditorSubmitError,
  humanizeFlyerImportError,
  humanizePosterUploadError,
  mapDraftFieldToFlyerField,
  mergeDraftWithExtracted,
  normalizeFlyerComparableValue,
  toEventImageInput,
  uploadPosterImmediately,
  parseNullableFloat,
  parseNullableInt,
} from '@/features/events/editor/utils';
import { useEventEditorTransport } from '@/features/events/editor/useEventEditorTransport';
import * as React from 'react';
import {
  Alert as NativeAlert,
  Image,
  Keyboard,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  View,
} from 'react-native';

export function EventEditor({
  mode,
  eventID,
  initialEvent,
  isLoadingInitial = false,
  createPrefill = null,
}: EventEditorProps) {
  const auth = useAuth();
  const editorTransport = useEventEditorTransport();
  const ensureWritableMarket = useWritableMarketGuard();
  const isDesktop = useIsDesktop();
  const [step, setStep] = React.useState(0);
  const [error, setError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [activePicker, setActivePicker] = React.useState<PickerField | null>(null);
  const [hasPinnedLocation, setHasPinnedLocation] = React.useState(true);
  const [addressSuggestions, setAddressSuggestions] = React.useState<GeocodeSuggestion[]>([]);
  const [isLoadingAddressSuggestions, setIsLoadingAddressSuggestions] = React.useState(false);
  const [isResolvingAddress, setIsResolvingAddress] = React.useState(false);
  const [addressLookupError, setAddressLookupError] = React.useState<string | null>(null);
  const [shouldSuggestAddress, setShouldSuggestAddress] = React.useState(false);
  const scrollViewRef = React.useRef<ScrollView | null>(null);
  const autocompleteRequestRef = React.useRef(0);
  const geocodeRequestRef = React.useRef(0);
  const scheduleDefaults = React.useMemo(getInitialSchedule, []);
  const [draft, setDraft] = React.useState<EventDraft>(() =>
    buildEventDraft({ mode, initialEvent, scheduleDefaults, createPrefill })
  );
  const { membershipsQuery } = useAccountContext();
  const tagsQuery = useEventTagCatalog();
  const attributesQuery = useEventAttributeCatalog();
  const [activePosterCropEditor, setActivePosterCropEditor] = React.useState<ActivePosterCropEditor | null>(null);
  const [isImportingFlyer, setIsImportingFlyer] = React.useState(false);
  const [flyerNotice, setFlyerNotice] = React.useState<string | null>(null);
  const [flyerImportError, setFlyerImportError] = React.useState<string | null>(null);
  const [flyerDraftMetadata, setFlyerDraftMetadata] = React.useState<FlyerDraftMetadata | null>(
    null
  );
  const posterUploadRequestsRef = React.useRef<Record<string, XMLHttpRequest>>({});
  const isUploadingPoster = draft.posters.some((poster) => poster.status === 'uploading');
  const hasFailedPosterUploads = draft.posters.some((poster) => poster.status === 'failed');
  const uploadedPosterCount = draft.posters.filter((poster) => poster.status === 'uploaded').length;
  const selectedVenueMembership = React.useMemo(
    () =>
      draft.venueId
        ? membershipsQuery.data?.memberships.find((membership) =>
            membership.venues.some((venue) => venue.venue_id === draft.venueId)
          ) ?? null
        : null,
    [draft.venueId, membershipsQuery.data?.memberships]
  );
  const linkedVenueID = selectedVenueMembership ? draft.venueId : null;
  const isCheckingSelectedVenueAccess = Boolean(
    draft.venueId && (membershipsQuery.isPending || membershipsQuery.isFetching)
  );
  const flyerAssistOrgQuery = useOrganization(
    selectedVenueMembership?.organization_id ?? '',
    Boolean(selectedVenueMembership?.organization_id)
  );
  const canUseFlyerAssist = hasIncludedOrganizationFeature(
    flyerAssistOrgQuery.data?.feature_access,
    OrganizationPlanFeatureAIFlyerExtraction
  );
  const flyerAssistLockedReason = React.useMemo(() => {
    if (mode !== 'create') {
      return null;
    }
    if (!draft.venueId) {
      return 'Choose a linked venue to use flyer assist.';
    }
    if (membershipsQuery.isPending || membershipsQuery.isFetching) {
      return 'Checking venue access...';
    }
    if (!selectedVenueMembership) {
      return 'Flyer assist works for linked venue teams on Growth.';
    }
    if (flyerAssistOrgQuery.isPending || flyerAssistOrgQuery.isFetching) {
      return 'Checking Growth access...';
    }
    if (!canUseFlyerAssist) {
      return 'Flyer assist is a Growth feature for venue teams.';
    }
    return null;
  }, [
    canUseFlyerAssist,
    draft.venueId,
    flyerAssistOrgQuery.isFetching,
    flyerAssistOrgQuery.isPending,
    membershipsQuery.isFetching,
    membershipsQuery.isPending,
    mode,
    selectedVenueMembership,
  ]);

  React.useEffect(() => {
    if (mode !== 'edit' || !initialEvent || isSubmitting) {
      return;
    }

    setDraft(buildEventDraft({ mode, initialEvent, scheduleDefaults }));
    setStep(0);
    setError(null);
    setActivePicker(null);
    setHasPinnedLocation(
      hasCoordinate(initialEvent.coordinate.latitude, initialEvent.coordinate.longitude)
    );
    setAddressSuggestions([]);
    setIsLoadingAddressSuggestions(false);
    setIsResolvingAddress(false);
    setAddressLookupError(null);
    setShouldSuggestAddress(false);
    Object.values(posterUploadRequestsRef.current).forEach((request) => request.abort());
    posterUploadRequestsRef.current = {};
    setActivePosterCropEditor(null);
    setIsImportingFlyer(false);
    setFlyerNotice(null);
    setFlyerImportError(null);
    setFlyerDraftMetadata(null);
  }, [createPrefill, initialEvent, isSubmitting, mode, scheduleDefaults]);

  React.useEffect(() => {
    trackLaunchEvent(mode === 'edit' ? 'edit_event_open' : 'create_event_open', {
      screen: mode === 'edit' ? 'events/[id]/edit' : 'events/create',
      event_id: eventID,
    });
  }, [eventID, mode]);

  React.useEffect(() => {
    const postersMissingSize = draft.posters.filter(
      (poster) => (!poster.width || !poster.height) && (poster.publicURL || poster.uri)
    );
    if (!postersMissingSize.length) {
      return;
    }

    let isCancelled = false;
    postersMissingSize.forEach((poster) => {
      Image.getSize(
        poster.publicURL ?? poster.uri,
        (width, height) => {
          if (isCancelled) {
            return;
          }
          setDraft((current) => ({
            ...current,
            posters: current.posters.map((currentPoster) =>
              currentPoster.localID === poster.localID
                ? { ...currentPoster, width, height }
                : currentPoster
            ),
          }));
        },
        () => undefined
      );
    });

    return () => {
      isCancelled = true;
    };
  }, [draft.posters]);

  React.useEffect(
    () => () => {
      Object.values(posterUploadRequestsRef.current).forEach((request) => request.abort());
      posterUploadRequestsRef.current = {};
    },
    []
  );

  React.useEffect(() => {
    if (!shouldSuggestAddress) {
      return;
    }

    const query = draft.address.trim();
    if (query.length < 3) {
      setIsLoadingAddressSuggestions(false);
      setAddressSuggestions([]);
      return;
    }

    const timeoutID = setTimeout(() => {
      const requestID = autocompleteRequestRef.current + 1;
      autocompleteRequestRef.current = requestID;
      setIsLoadingAddressSuggestions(true);

      const coords = locationStore.getState().coords;
      editorTransport
        .autocompleteAddress(query, coords)
        .then((results) => {
          if (autocompleteRequestRef.current !== requestID) {
            return;
          }
          setAddressSuggestions(results);
        })
        .catch(() => {
          if (autocompleteRequestRef.current !== requestID) {
            return;
          }
          setAddressSuggestions([]);
        })
        .finally(() => {
          if (autocompleteRequestRef.current === requestID) {
            setIsLoadingAddressSuggestions(false);
          }
        });
    }, 400);

    return () => clearTimeout(timeoutID);
  }, [draft.address, editorTransport, shouldSuggestAddress]);

  const validationIssues = React.useMemo(
    () => (error ? getEventEditorStepIssues(step, draft, hasPinnedLocation) : []),
    [draft, error, hasPinnedLocation, step],
  );

  React.useEffect(() => {
    if (!error) {
      return;
    }
    const nextError = validationIssues[0]?.message ?? null;
    if (nextError !== error) {
      setError(nextError);
    }
  }, [error, validationIssues]);

  if (auth.isHydrating || isLoadingInitial) {
    return (
      <AppScreen>
        <LoadingState
          label={mode === 'edit' ? 'Loading event editor...' : 'Loading event composer...'}
        />
      </AppScreen>
    );
  }

  if (!auth.session) {
    return <Redirect href="/auth" />;
  }

  const startsAt = buildDateTime(draft.startsDate, draft.startsTime);
  const endsAt = buildDateTime(draft.endsDate, draft.endsTime);
  const activeCropPoster = activePosterCropEditor
    ? draft.posters.find((poster) => poster.localID === activePosterCropEditor.localID) ?? null
    : null;

  function updateDraft(patch: Partial<EventDraft>) {
    setDraft((current) => ({ ...current, ...patch }));
  }

  function trackFlyerManualOverride(field: FlyerOverrideField, nextValue: string) {
    if (!flyerDraftMetadata?.importedValues[field]) {
      return;
    }

    if (flyerDraftMetadata.trackedOverrides.includes(field)) {
      return;
    }

    if (flyerDraftMetadata.importedValues[field] === nextValue) {
      return;
    }

    trackLaunchEvent('flyer_manual_override', {
      screen: 'events/create',
      field,
    });

    setFlyerDraftMetadata((current) => {
      if (!current || current.trackedOverrides.includes(field)) {
        return current;
      }
      return {
        ...current,
        trackedOverrides: [...current.trackedOverrides, field],
      };
    });
  }

  function handleDraftFieldChange<K extends keyof EventDraft>(field: K, value: EventDraft[K]) {
    const patch = { [field]: value } as Partial<EventDraft>;
    updateDraft(patch);

    const trackedField = mapDraftFieldToFlyerField(field);
    if (!trackedField) {
      return;
    }

    const normalizedValue = normalizeFlyerComparableValue(field, value);
    if (normalizedValue !== null) {
      trackFlyerManualOverride(trackedField, normalizedValue);
    }
  }

  function handlePickerChange(field: PickerField, nextValue: Date) {
    const patch = applyPickerValue(field, nextValue, draft);
    updateDraft(patch);

    const draftField = field;
    const nextFieldValue = patch[draftField];
    if (typeof nextFieldValue === 'string') {
      const trackedField = mapDraftFieldToFlyerField(draftField);
      if (trackedField) {
        trackFlyerManualOverride(trackedField, nextFieldValue);
      }
    }
  }

  function handleAddressChange(value: string) {
    setAddressLookupError(null);
    setHasPinnedLocation(false);
    setAddressSuggestions([]);
    setShouldSuggestAddress(true);
    updateDraft({
      address: value,
      latitude: 0,
      longitude: 0,
    });
    trackFlyerManualOverride('address', value);
  }

  function handleVenueSelect(venue: VenueResult) {
    setAddressLookupError(null);
    setAddressSuggestions([]);
    setShouldSuggestAddress(false);

    const nextPatch: Partial<EventDraft> = {
      venueName: venue.name,
      venueId: venue.id ?? null,
    };

    if (venue.address?.trim()) {
      nextPatch.address = venue.address.trim();
    }

    if (hasCoordinate(venue.lat ?? 0, venue.lng ?? 0)) {
      nextPatch.latitude = venue.lat ?? 0;
      nextPatch.longitude = venue.lng ?? 0;
      setHasPinnedLocation(true);
    } else if (venue.address?.trim()) {
      nextPatch.latitude = 0;
      nextPatch.longitude = 0;
      setHasPinnedLocation(false);
    }

    updateDraft(nextPatch);
  }

  async function handleAddressSuggestionSelect(suggestion: GeocodeSuggestion) {
    const requestID = geocodeRequestRef.current + 1;
    geocodeRequestRef.current = requestID;

    setAddressSuggestions([]);
    setShouldSuggestAddress(false);
    setHasPinnedLocation(true);
    setIsResolvingAddress(true);
    setAddressLookupError(null);
    updateDraft({
      address: suggestion.display_name,
      latitude: suggestion.lat,
      longitude: suggestion.lng,
    });

    try {
      const result = await editorTransport.geocodeAddress(suggestion.display_name);

      if (geocodeRequestRef.current !== requestID) {
        return;
      }

      setHasPinnedLocation(true);
      setDraft((current) => {
        if (current.address !== suggestion.display_name) {
          return current;
        }

        return {
          ...current,
          latitude: result.lat,
          longitude: result.lng,
        };
      });
    } catch (geocodeError) {
      if (geocodeRequestRef.current !== requestID) {
        return;
      }

      setHasPinnedLocation(false);
      setAddressLookupError(
        geocodeError instanceof APIError && geocodeError.status === 404
          ? 'We could not place that address on the map yet.'
          : 'Geocoding service unavailable right now.'
      );
    } finally {
      if (geocodeRequestRef.current === requestID) {
        setIsResolvingAddress(false);
      }
    }
  }

  function openPicker(field: PickerField) {
    if (Platform.OS === 'web') {
      return;
    }

    Keyboard.dismiss();

    const modeForPicker = getPickerMode(field);
    const value = getPickerValue(field, draft);

    if (Platform.OS === 'android') {
      setActivePicker(null);
      requestAnimationFrame(() => {
        DateTimePickerAndroid.open({
          value,
          mode: modeForPicker,
          is24Hour: true,
          onChange: (event, selectedDate) => {
            if (event.type !== 'set' || !selectedDate) {
              return;
            }
            handlePickerChange(field, selectedDate);
          },
        });
      });
      return;
    }

    setActivePicker((current) => (current === field ? null : field));
  }

  function closePicker() {
    setActivePicker(null);
  }

  function scrollEditorToTop() {
    requestAnimationFrame(() => {
      scrollViewRef.current?.scrollTo({ y: 0, animated: true });
    });
  }

  function goToStep(nextStep: number) {
    setError(null);
    setActivePicker(null);
    setStep(nextStep);
    scrollEditorToTop();
  }

  function handleNext() {
    const validationError = validateEventEditorStep(step, draft, hasPinnedLocation);
    if (validationError) {
      setError(validationError);
      return;
    }
    goToStep(Math.min(step + 1, STEPS.length - 1));
  }

  function handleBack() {
    if (step > 0) {
      goToStep(Math.max(step - 1, 0));
      return;
    }

    if (router.canGoBack()) {
      router.back();
      return;
    }

    if (mode === 'edit' && eventID) {
      router.replace(getEventHref(eventID) as never);
      return;
    }

    router.replace('/(tabs)/events');
  }

  async function chooseImageAssets(maxSelection = EVENT_POSTER_CONSTANTS.MAX_IMAGES_PER_EVENT) {
    const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!permission.granted) {
      throw new Error('Photo library access is required to select a flyer.');
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ['images'],
      quality: 0.85,
      allowsEditing: false,
      allowsMultipleSelection: maxSelection > 1,
      selectionLimit: maxSelection,
    });

    if (result.canceled || !result.assets.length) {
      return [];
    }

    return result.assets.slice(0, maxSelection);
  }

  function updatePoster(localID: string, patch: Partial<DraftPoster>) {
    setDraft((current) => ({
      ...current,
      posters: current.posters.map((poster) =>
        poster.localID === localID ? { ...poster, ...patch } : poster
      ),
    }));
  }

  async function startPosterUpload(localID: string, asset: ImagePickerAsset) {
    if (!auth.session?.accessToken) {
      updatePoster(localID, {
        status: 'failed',
        progress: 0,
        uploadError: 'Your session expired. Sign in again before uploading another poster.',
      });
      return;
    }

    updatePoster(localID, {
      status: 'uploading',
      progress: 0.02,
      uploadError: null,
    });

    try {
      const currentAccessToken = await getPosterUploadAccessToken();
      const data = await uploadPosterImmediately({
        accessToken: currentAccessToken,
        asset,
        onProgress: (progress) => {
          updatePoster(localID, {
            progress: progress >= 1 ? 0.98 : Math.max(progress, 0.02),
          });
        },
        onReady: (request) => {
          posterUploadRequestsRef.current[localID] = request;
        },
      });
      delete posterUploadRequestsRef.current[localID];
      updatePoster(localID, {
        status: 'uploaded',
        progress: 1,
        objectKey: data.object_key,
        publicURL: data.url,
        warnings: data.warnings ?? [],
        uploadError: null,
      });
    } catch (error) {
      delete posterUploadRequestsRef.current[localID];
      if (error instanceof Error && error.message === 'upload_aborted') {
        return;
      }
      captureSentryException(error, {
        tags: { area: 'upload', asset: 'flyer' },
        extra: {
          fileName: asset.fileName ?? 'poster.jpg',
          mimeType: asset.mimeType ?? 'image/jpeg',
        },
      });
      updatePoster(localID, {
        status: 'failed',
        progress: 0,
        uploadError: humanizePosterUploadError(error),
      });
    }
  }

  async function getPosterUploadAccessToken() {
    await auth.request('/me');
    return authStore.getState().session?.accessToken ?? auth.session!.accessToken;
  }

  async function pickPosters() {
    setError(null);
    const remainingSlots = EVENT_POSTER_CONSTANTS.MAX_IMAGES_PER_EVENT - draft.posters.length;
    if (remainingSlots <= 0) {
      return;
    }

    const assets = await chooseImageAssets(remainingSlots);
    if (!assets.length) {
      return;
    }

    const nextDraftPosters = assets.map((asset) => buildUploadingDraftPoster(asset));
    setDraft((current) => ({
      ...current,
      posters: [...current.posters, ...nextDraftPosters],
    }));
    nextDraftPosters.forEach((poster) => {
      void startPosterUpload(poster.localID, poster.asset!);
    });
  }

  async function replaceDraftPostersWithAsset(asset: ImagePickerAsset) {
    Object.values(posterUploadRequestsRef.current).forEach((request) => request.abort());
    posterUploadRequestsRef.current = {};
    const nextPoster = buildUploadingDraftPoster(asset);
    setDraft((current) => ({ ...current, posters: [nextPoster] }));
    await startPosterUpload(nextPoster.localID, asset);
  }

  function handleRemovePoster(localID: string) {
    const uploadRequest = posterUploadRequestsRef.current[localID];
    if (uploadRequest) {
      uploadRequest.abort();
      delete posterUploadRequestsRef.current[localID];
    }
    setDraft((current) => ({
      ...current,
      posters: current.posters.filter((poster) => poster.localID !== localID),
    }));
    if (activePosterCropEditor?.localID === localID) {
      setActivePosterCropEditor(null);
    }
  }

  async function handleRetryPoster(localID: string) {
    const poster = draft.posters.find((candidate) => candidate.localID === localID);
    if (!poster?.asset) {
      return;
    }
    await startPosterUpload(localID, poster.asset);
  }

  function handleReorderPosters(nextOrder: string[]) {
    setDraft((current) => ({
      ...current,
      posters: nextOrder.flatMap((localID) => {
        const poster = current.posters.find((candidate) => candidate.localID === localID);
        return poster ? [poster] : [];
      }),
    }));
  }

  async function handleSavePosterFocalPoint(localID: string, focalPointX: number, focalPointY: number, focalScale: number) {
    updatePoster(localID, { focalPointX, focalPointY, focalScale });
    setActivePosterCropEditor(null);

    const poster = draft.posters.find((candidate) => candidate.localID === localID);
    if (
      mode !== 'edit' ||
      !initialEvent?.id ||
      !initialEvent.published_at ||
      initialEvent.status !== EventStatusActive ||
      !poster?.imageID
    ) {
      return;
    }

    try {
      const payload: PatchEventPosterFocalPointRequest = {
        focal_point_x: focalPointX,
        focal_point_y: focalPointY,
        focal_scale: focalScale,
      };
      const updatedImage = await editorTransport.savePosterFocalPoint(
        initialEvent.id,
        poster.imageID,
        payload
      );
      updatePoster(localID, {
        focalPointX: updatedImage.focal_point_x,
        focalPointY: updatedImage.focal_point_y,
        focalScale: updatedImage.focal_scale ?? focalScale,
      });
    } catch {
      setError('We couldn’t save that crop yet. Try again in a moment.');
    }
  }

  async function confirmReplaceFlyerDraft() {
    if (!flyerDraftMetadata && !draft.posters.length) {
      return true;
    }

    if (Platform.OS === 'web' && typeof globalThis.confirm === 'function') {
      return globalThis.confirm('Replace the current flyer draft with a new flyer?');
    }

    return await new Promise<boolean>((resolve) => {
      NativeAlert.alert(
        'Replace flyer draft?',
        'Using another flyer will replace the current drafted flyer details and poster.',
        [
          { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
          { text: 'Replace', style: 'destructive', onPress: () => resolve(true) },
        ]
      );
    });
  }

  async function resolveImportedAddress(address: string) {
    try {
      const result = await editorTransport.geocodeAddress(address);

      setHasPinnedLocation(true);
      setAddressLookupError(null);
      setDraft((current) => ({
        ...current,
        latitude: result.lat,
        longitude: result.lng,
      }));
    } catch (geocodeError) {
      setHasPinnedLocation(false);
      setAddressLookupError(
        geocodeError instanceof APIError && geocodeError.status === 404
          ? 'We could not place that address on the map yet.'
          : 'Geocoding service unavailable right now.'
      );
    }
  }

  async function handleFlyerImport() {
    setError(null);
    setFlyerImportError(null);
    setFlyerNotice(null);

    if (flyerAssistLockedReason) {
      setFlyerImportError(flyerAssistLockedReason);
      return;
    }

    if (!(await confirmReplaceFlyerDraft())) {
      return;
    }

    const assets = await chooseImageAssets(1);
    if (!assets.length) {
      return;
    }
    const asset = assets[0];

    setIsImportingFlyer(true);
    trackLaunchEvent('flyer_import_started', {
      screen: 'events/create',
    });

    try {
      await replaceDraftPostersWithAsset(asset);

      const response = await extractFlyerFromImage({
        requestRaw: auth.requestRaw,
        uri: asset.uri,
        mimeType: asset.mimeType,
        name: asset.fileName,
        venueID: draft.venueId,
      });

      const merged = mergeDraftWithExtracted({
        draft,
        extracted: response.extracted,
        scheduleDefaults,
      });

      setDraft((current) => ({ ...current, ...merged.patch }));
      setFlyerDraftMetadata({
        extracted: response.extracted,
        duplicate: response.duplicate ?? null,
        appliedFields: merged.appliedFields,
        needsReview: merged.needsReview,
        importedValues: merged.importedValues,
        trackedOverrides: [],
      });

      if (merged.patch.address) {
        await resolveImportedAddress(merged.patch.address);
      }

      setStep(0);
      setFlyerNotice('Drafted from flyer. Review details before publishing.');
      trackLaunchEvent('flyer_import_succeeded', {
        screen: 'events/create',
        duplicate: Boolean(response.duplicate?.event_id),
        applied_field_count: merged.appliedFields.length,
      });
      if (response.duplicate?.event_id) {
        trackLaunchEvent('flyer_duplicate_detected', {
          screen: 'events/create',
          duplicate_event_id: response.duplicate.event_id,
        });
      }
    } catch (importError) {
      setFlyerImportError(humanizeFlyerImportError(importError));
      setFlyerNotice(null);
      trackLaunchEvent('flyer_import_failed', {
        screen: 'events/create',
      });
    } finally {
      setIsImportingFlyer(false);
    }
  }

  async function clearFlyerDraft() {
    const shouldClear =
      Platform.OS === 'web' && typeof globalThis.confirm === 'function'
        ? globalThis.confirm('Clear the drafted flyer details and start over manually?')
        : await new Promise<boolean>((resolve) => {
            NativeAlert.alert(
              'Clear flyer draft?',
              'This resets the drafted flyer details and poster so you can start manually.',
              [
                { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
                { text: 'Clear', style: 'destructive', onPress: () => resolve(true) },
              ]
            );
          });

    if (!shouldClear) {
      return;
    }

    setDraft(buildEventDraft({ mode: 'create', scheduleDefaults, createPrefill }));
    setHasPinnedLocation(false);
    setAddressSuggestions([]);
    setAddressLookupError(null);
    setShouldSuggestAddress(false);
    setActivePicker(null);
    setStep(0);
    setFlyerDraftMetadata(null);
    setFlyerNotice(null);
    setFlyerImportError(null);
    trackLaunchEvent('flyer_draft_cleared', {
      screen: 'events/create',
    });
  }

  async function submitEvent() {
    const validationError = validateEventEditorDraft(draft, hasPinnedLocation);
    if (validationError) {
      setError(validationError);
      return;
    }
    if (!startsAt) {
      setError('Enter a valid start time.');
      return;
    }
    if (hasPartialDateTime(draft.endsDate, draft.endsTime)) {
      setError('Finish the end date/time or remove it.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      addSentryActionBreadcrumb({
        screen: 'event_editor',
        action: mode === 'edit' ? 'save_event' : 'publish_event',
        data: {
          mode,
          event_id: eventID ?? null,
          has_venue_id: Boolean(linkedVenueID),
          tag_count: draft.tags.length,
          poster_count: draft.posters.length,
        },
      });
      if (!(await ensureWritableMarket())) {
        return;
      }
      if (isCheckingSelectedVenueAccess) {
        setError('Still checking venue access. Try again in a moment.');
        return;
      }

      const posterImages = await Promise.all(
        draft.posters.map((poster, index) => toEventImageInput(auth, poster, index))
      );
      const doorsAtISO =
        draft.doorsDate && draft.doorsTime
          ? (buildDateTime(draft.doorsDate, draft.doorsTime)?.toISOString() ?? null)
          : null;
      const lastEntryTimeISO =
        draft.lastEntryTime.trim() && draft.startsDate.trim()
            ? buildDateTime(draft.startsDate, draft.lastEntryTime)?.toISOString() ?? null
          : null;

      const payload: EventMutationPayload = {
        title: draft.title.trim(),
        description: draft.description.trim(),
        venue_name: draft.venueName.trim(),
        venue_id: linkedVenueID,
        address: draft.address.trim(),
        age_restriction: draft.ageRestriction,
        latitude: draft.latitude,
        longitude: draft.longitude,
        starts_at: startsAt.toISOString(),
        ends_at: endsAt ? endsAt.toISOString() : null,
        capacity: parseNullableInt(draft.capacity),
        tags: draft.tags,
        attributes: draft.attributes,
        poster_images: posterImages,
        event_url: draft.entryType === EntryTypeTicketed ? null : draft.eventUrl.trim() || null,
        ticket_url: draft.entryType === EntryTypeTicketed ? draft.ticketUrl.trim() || null : null,
        doors_at: doorsAtISO,
        entry_type: draft.entryType,
        cover_price: parseNullableFloat(draft.coverPrice),
        cover_notes: draft.coverNotes.trim() || null,
        rsvp_limit: parseNullableInt(draft.rsvpLimit),
        is_private: false,
        last_entry_time: lastEntryTimeISO,
        short_description: draft.shortDescription.trim() || null,
        language: draft.language.trim() || 'en',
        event_format: draft.eventFormat,
        dress_code: draft.dressCode.trim() || null,
        parking_info: draft.parkingInfo.trim() || null,
        presented_by: draft.presentedBy.trim() || null,
        weather_dependent: draft.weatherDependent,
        rain_plan: draft.weatherDependent ? draft.rainPlan.trim() || null : null,
        has_radious_tickets: false,
        cross_post_meta: false,
        contact_email: draft.contactEmail.trim() || null,
        contact_phone: draft.contactPhone.trim() || null,
        internal_notes: draft.internalNotes.trim() || null,
        series_id: draft.seriesId.trim() || null,
      };

      const event = await editorTransport.saveEvent({ eventID, mode, payload });

      if (mode === 'edit' && initialEvent) {
        trackEventEdited({
          eventID: event.id,
          venueID: linkedVenueID ?? initialEvent.venue_id ?? null,
          fieldsChanged: getEditedEventFields(
          initialEvent,
          draft,
          payload.starts_at,
          payload.ends_at ?? null,
          doorsAtISO,
          lastEntryTimeISO,
        ),
      });
      } else {
        trackEventPublished(event.id, linkedVenueID);
      }

      if (mode === 'edit') {
        if (router.canGoBack()) {
          router.back();
        } else {
          router.replace(getEventHref(event.id, event.title) as never);
        }
        return;
      }

      router.replace(getEventHref(event.id, event.title) as never);
    } catch (submitError) {
      if (!handleReadOnlyMarketError(submitError)) {
        setError(humanizeEventEditorSubmitError(submitError, mode));
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AppScreen>
      <KeyboardAvoidingView
        className="flex-1"
        style={{ flex: 1 }}
        behavior={undefined}>
      <ScrollView
        ref={scrollViewRef}
        className="flex-1"
        style={{ flex: 1 }}
        automaticallyAdjustKeyboardInsets={Platform.OS === 'ios'}
        contentContainerStyle={{ flexGrow: 1, paddingBottom: 64 }}
        keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}>
        <View
          style={
            isDesktop
              ? { maxWidth: 680, width: '100%', alignSelf: 'center', paddingHorizontal: 24, paddingBottom: 16, paddingTop: 12 }
              : undefined
          }
          className={isDesktop ? 'flex-1' : 'flex-1 px-4 pb-4 pt-3'}>
          <View className="mb-5 flex-row items-center gap-3">
            <Pressable
              onPress={handleBack}
              className="size-10 items-center justify-center rounded-full border border-white/10 bg-white/5">
              <Icon as={ArrowLeftIcon} size={18} color={DESIGN_TOKENS.colors.text} />
            </Pressable>
            <View className="flex-1">
              <Text variant="large" className="text-lg font-semibold text-foreground">
                {mode === 'edit' ? 'Edit event' : 'Host an event'}
              </Text>
              <Text variant="small" className="text-muted-foreground">
                {STEPS[step]}
              </Text>
            </View>
          </View>

          <View className="mb-6 flex-row items-center gap-1.5">
            {STEPS.map((label, index) => (
              <Pressable
                key={`dot-${label}`}
                disabled={index > step}
                onPress={index <= step ? () => goToStep(index) : undefined}
                className="h-1.5 flex-1 rounded-full"
                style={{
                  backgroundColor:
                    index <= step ? DESIGN_TOKENS.colors.accentStrong : DESIGN_TOKENS.colors.borderStrong,
                }}
              />
            ))}
          </View>

          <PostingContextCard draft={draft} linkedVenueID={linkedVenueID} />

          {flyerNotice && !flyerDraftMetadata ? (
            <View className="mb-4">
              <Alert icon={InfoIcon}>
                <AlertTitle>Drafted From Flyer</AlertTitle>
                <AlertDescription>{flyerNotice}</AlertDescription>
              </Alert>
            </View>
          ) : null}

          {flyerImportError ? (
            <View className="mb-4">
              <Alert icon={AlertCircleIcon} variant="destructive">
                <AlertTitle>Flyer Import Unavailable</AlertTitle>
                <AlertDescription>{flyerImportError}</AlertDescription>
              </Alert>
            </View>
          ) : null}

          {flyerDraftMetadata?.duplicate?.event_id ? (
            <View className="mb-4 gap-3 rounded-[18px] border p-4" style={sectionInsetStyle()}>
              <Text variant="small" className="font-semibold text-foreground">
                Similar event already exists
              </Text>
              <Text variant="small" className="mt-1 text-muted-foreground">
                {flyerDraftMetadata.duplicate.event_title ?? 'An existing event'} looks close to this flyer.
              </Text>
              <View className="mt-3 flex-row gap-3">
                <Button
                  className="flex-1"
                  onPress={() =>
                    router.push(getEventHref(flyerDraftMetadata.duplicate?.event_id ?? '') as never)
                  }>
                  <Text>View existing</Text>
                </Button>
                <Pressable
                  className="flex-1 items-center justify-center rounded-[18px] border px-4 py-3"
                  style={{ borderColor: DESIGN_TOKENS.colors.borderStrong, backgroundColor: DESIGN_TOKENS.colors.surfaceAlt }}
                  onPress={() => goToStep(0)}>
                  <Text variant="small" className="font-semibold text-foreground">Keep editing</Text>
                </Pressable>
              </View>
            </View>
          ) : null}

          {flyerDraftMetadata ? (
            <View className="mb-4 flex-row flex-wrap gap-2 rounded-[14px] border p-3" style={sectionInsetStyle()}>
              <Text variant="small" className="w-full text-muted-foreground">
                Drafted from flyer. Imported values stay editable.
              </Text>
              <Pressable
                className="rounded-full border px-3 py-2"
                style={{ borderColor: DESIGN_TOKENS.colors.borderStrong, backgroundColor: DESIGN_TOKENS.colors.surfaceAlt }}
                disabled={isImportingFlyer || isUploadingPoster}
                onPress={() => handleFlyerImport().catch(() => undefined)}>
                <Text variant="small" className="font-semibold text-foreground">Use another flyer</Text>
              </Pressable>
              <Pressable
                className="rounded-full border px-3 py-2"
                style={{ borderColor: DESIGN_TOKENS.colors.borderStrong, backgroundColor: DESIGN_TOKENS.colors.surfaceAlt }}
                onPress={() => clearFlyerDraft().catch(() => undefined)}>
                <Text variant="small" className="font-semibold text-foreground">Clear</Text>
              </Pressable>
            </View>
          ) : null}

          {step === 0 ? (
            <PitchStep
              draft={draft}
              mode={mode}
              flyerReview={flyerDraftMetadata}
              tagGroups={tagsQuery.data ?? []}
              isLoadingTags={tagsQuery.isLoading}
              tagLoadError={tagsQuery.error instanceof Error ? 'Unable to load tags right now.' : null}
              isUploadingPoster={isUploadingPoster}
              isImportingFlyer={isImportingFlyer}
              flyerAssistLockedReason={flyerAssistLockedReason}
              posterLimit={EVENT_POSTER_CONSTANTS.MAX_IMAGES_PER_EVENT}
              onChange={(patch) => {
                for (const [field, value] of Object.entries(patch) as [keyof EventDraft, EventDraft[keyof EventDraft]][]) {
                  handleDraftFieldChange(field, value);
                }
              }}
              onPickPosters={() => pickPosters().catch(() => undefined)}
              onRemovePoster={handleRemovePoster}
              onRetryPoster={(localID) => handleRetryPoster(localID).catch(() => undefined)}
              onOpenPosterCrop={(localID) => {
                const poster = draft.posters.find((candidate) => candidate.localID === localID);
                if (!poster || poster.status !== 'uploaded') {
                  return;
                }
                setActivePosterCropEditor({
                  localID,
                  focalPointX: poster.focalPointX,
                  focalPointY: poster.focalPointY,
                  focalScale: poster.focalScale,
                });
              }}
              onReorderPosters={handleReorderPosters}
              onFlyerImport={() => handleFlyerImport().catch(() => undefined)}
            />
          ) : null}

          {step === 1 ? (
            <ScheduleStep
              draft={draft}
              flyerReview={flyerDraftMetadata}
              addressSuggestions={addressSuggestions}
              isLoadingAddressSuggestions={isLoadingAddressSuggestions}
              hasPinnedLocation={hasPinnedLocation}
              isResolvingAddress={isResolvingAddress}
              addressLookupError={addressLookupError}
              activePicker={activePicker}
              validationIssues={validationIssues}
              onChange={(patch) => {
                for (const [field, value] of Object.entries(patch) as [keyof EventDraft, EventDraft[keyof EventDraft]][]) {
                  handleDraftFieldChange(field, value);
                }
              }}
              onVenueSelect={handleVenueSelect}
              onAddressChange={handleAddressChange}
              onSelectAddressSuggestion={handleAddressSuggestionSelect}
              onOpenPicker={openPicker}
              onClosePicker={closePicker}
              onPickerChange={handlePickerChange}
            />
          ) : null}

          {step === 2 ? (
            <FinishStep
              mode={mode}
              draft={draft}
              linkedVenueID={linkedVenueID}
              startsAt={startsAt}
              flyerReview={flyerDraftMetadata}
              attributes={attributesQuery.data ?? []}
              isLoadingAttributes={attributesQuery.isLoading}
              attributeLoadError={
                attributesQuery.error instanceof Error ? 'Unable to load attributes right now.' : null
              }
              activePicker={activePicker}
              onChange={(patch) => {
                for (const [field, value] of Object.entries(patch) as [keyof EventDraft, EventDraft[keyof EventDraft]][]) {
                  handleDraftFieldChange(field, value);
                }
              }}
              onOpenPicker={openPicker}
              onClosePicker={closePicker}
              onPickerChange={handlePickerChange}
            />
          ) : null}

          {error ? (
            <View className="mt-6 rounded-[20px] border border-[#ef6a3d]/40 bg-[#ef6a3d]/10 px-4 py-3">
              <Text variant="small" className="text-foreground">{error}</Text>
            </View>
          ) : null}

          <View className="mt-3 flex-row gap-3">
            <Pressable className="size-12 items-center justify-center" onPress={handleBack}>
              <Icon as={ArrowLeftIcon} size={20} color={DESIGN_TOKENS.colors.textSoft} />
            </Pressable>
            {step < STEPS.length - 1 ? (
              <Button className="flex-1" onPress={handleNext}>
                <Icon as={MoveRightIcon} size={16} className="text-[#111318]" />
                <Text>Continue</Text>
              </Button>
            ) : (
              <Button
                className="flex-1"
                onPress={() => submitEvent().catch(() => undefined)}
                disabled={isSubmitting || isUploadingPoster || hasFailedPosterUploads || uploadedPosterCount === 0}>
                <Icon as={MoveRightIcon} size={16} className="text-[#111318]" />
                <Text>
                  {isSubmitting
                    ? mode === 'edit'
                      ? 'Saving...'
                      : 'Publishing...'
                    : isUploadingPoster
                      ? 'Uploading posters...'
                      : uploadedPosterCount === 0
                        ? 'Add a poster to publish'
                        : hasFailedPosterUploads
                          ? 'Fix poster uploads'
                          : mode === 'edit'
                            ? 'Save changes'
                            : 'Publish event'}
                </Text>
              </Button>
            )}
          </View>
        </View>
      </ScrollView>
      </KeyboardAvoidingView>
      {activePosterCropEditor && activeCropPoster ? (
        <PosterFocalPointModal
          poster={activeCropPoster}
          visible
          focalPointX={activePosterCropEditor.focalPointX}
          focalPointY={activePosterCropEditor.focalPointY}
          focalScale={activePosterCropEditor.focalScale}
          onClose={() => setActivePosterCropEditor(null)}
          onDone={(nextX, nextY, nextScale) => {
            void handleSavePosterFocalPoint(
              activePosterCropEditor.localID,
              nextX,
              nextY,
              nextScale
            );
          }}
        />
      ) : null}
    </AppScreen>
  );
}

function PostingContextCard({
  draft,
  linkedVenueID,
}: {
  draft: EventDraft;
  linkedVenueID: string | null;
}) {
  const venueName = draft.venueName.trim();
  const title = linkedVenueID
    ? `Official venue event${venueName ? ` at ${venueName}` : ''}`
    : venueName
      ? `Personal event at ${venueName}`
      : 'Posting personally';
  const description = linkedVenueID
    ? 'This will appear as a venue-managed event for people discovering the venue.'
    : venueName
      ? 'Guests will see this venue as the location. The event is posted from your account, not from the venue.'
      : 'Choose a venue or enter an address so guests know where to go.';

  return (
    <View className="mb-4 rounded-[18px] border px-4 py-3" style={sectionInsetStyle()}>
      <View className="flex-row items-start gap-3">
        <View
          className="mt-0.5 size-7 items-center justify-center rounded-full"
          style={{ backgroundColor: DESIGN_TOKENS.colors.accentSoft }}>
          <Icon as={InfoIcon} size={15} color={DESIGN_TOKENS.colors.accentStrong} />
        </View>
        <View className="flex-1 gap-1">
          <Text variant="small" className="font-semibold text-foreground">{title}</Text>
          <Text variant="small" className="text-muted-foreground">{description}</Text>
        </View>
      </View>
    </View>
  );
}

function sectionInsetStyle() {
  return {
    borderColor: DESIGN_TOKENS.colors.borderStrong,
    backgroundColor: DESIGN_TOKENS.colors.surfaceAlt,
  } as const;
}
